import { Alert, Badge, Button, Card, Heading, Paper, Text } from '@components'
import { Microscope, Rocket, Ruler } from 'lucide-solid'
import { Show } from 'solid-js'
import { useService } from 'solid-services'
import { css } from 'styled-system/css'
import { Grid } from 'styled-system/jsx'
import { ZoomableImage } from '../../../components/custom/ZoomableImage'
import Visualisation from '../Visualisation'
import { useCreate } from './state'

const Image = (props: { src: string; title: string }) => {
	return (
		<Card.Root
			height="120px"
			cursor="pointer"
		>
			<Card.Header
				p={2}
				height="120px"
				display="grid"
			>
				<ZoomableImage src={props.src} />
			</Card.Header>
			<Card.Body
				pb={2}
				px={2}
			>
				<Text
					size="xs"
					textAlign="left"
					children={props.title}
				/>
			</Card.Body>
		</Card.Root>
	)
}

export default function Confirm() {
	const { newSetup, createSetupResource, submitSetup, isEditing } =
		useService(useCreate)()

	const entry = () =>
		newSetup()?.position === 'long' ? newSetup()?.high_3_price : newSetup()?.low_3_price
	const stop = () =>
		newSetup()?.position === 'long' ? newSetup()?.low_3_price : newSetup()?.high_3_price

	return (
		<Show
			when={newSetup()}
			fallback={<Text children="No setup provided" />}
		>
			<Grid gap={4}>
				<Grid gridTemplateColumns="1fr auto">
					<Heading>Symbol</Heading>
					<Text textAlign="right">{newSetup()?.symbol}</Text>
					<Heading>Exchange</Heading>
					<Text textAlign="right">{newSetup()?.exchange}</Text>
					<Heading>Market</Heading>
					<Text textAlign="right">{newSetup()?.market}</Text>
					<Heading>Position</Heading>
					<Badge
						background={newSetup()?.position === 'long' ? 'tscGreen.a5' : 'red.a5'}
						justifySelf="end"
					>
						{newSetup()?.position}
					</Badge>
					<Heading>Pattern Candlestick Time Frame</Heading>
					<Badge justifySelf="end">{newSetup()?.duration}</Badge>
				</Grid>

				<Grid
					class={css({
						gap: 4,
						gridTemplateColumns: {
							sm: '1fr 1fr',
						},
					})}
				>
					<Paper
						class={css({
							display: 'grid',
							justifyItems: 'center',
							alignContent: 'center',
						})}
					>
						<Visualisation
							setup={newSetup()}
							class={css({
								height: {
									mdTo2xl: '250px',
								},
							})}
						/>
					</Paper>

					<Paper>
						<Heading
							mb={3}
							children="HVF Levels"
						/>
						<Grid gridTemplateColumns="1fr 1fr">
							<Text>Entry:</Text>
							<Text textAlign="right">{entry()}</Text>
							<Text>Exit:</Text>
							<Text textAlign="right">{stop()}</Text>
							<Text>Interim 1:</Text>
							<Text textAlign="right">{newSetup()?.interim_1}</Text>
							<Text>Interim 2:</Text>
							<Text textAlign="right">{newSetup()?.interim_2}</Text>
							<Text>Target:</Text>
							<Text textAlign="right">{newSetup()?.target}</Text>
							<Text>Risk Reward:</Text>
							<Text textAlign="right">{newSetup()?.risk_reward_ratio}</Text>
							{/* <Text>Scale:</Text>
            <Text textAlign='right'>{newSetup()?.risk_reward_ratio}</Text> */}
						</Grid>
					</Paper>
				</Grid>

				<Grid
					gap={4}
					gridTemplateColumns="1fr 1fr 1fr 1fr"
				>
					<Image
						src={newSetup()?.image_macro!}
						title="Macro"
					/>
					<Image
						src={newSetup()?.image_pattern!}
						title="Pattern"
					/>
					<Image
						src={newSetup()?.image_trend!}
						title="Trend"
					/>
					<Image
						src={newSetup()?.image_trigger!}
						title="Trigger"
					/>
					<Show when={newSetup()?.image_time_stop}>
						<Image
							src={newSetup()?.image_time_stop!}
							title="Timestop"
						/>
					</Show>
					<Show when={newSetup()?.image_time_stop}>
						<Image
							src={newSetup()?.image_volume_by_price!}
							title="VBP"
						/>
					</Show>
				</Grid>

				<Grid>
					<Show when={newSetup()?.first_in_new_trend}>
						<Alert.Root borderColor="mint.10">
							<Alert.Icon asChild={(iconProps) => <Rocket {...iconProps()} />} />
							<Alert.Content>
								<Alert.Title>First in a new trend</Alert.Title>
								<Alert.Description>
									This HVF has the potential for overperformance
								</Alert.Description>
							</Alert.Content>
						</Alert.Root>
					</Show>

					<Show when={newSetup()?.has_primer}>
						<Alert.Root borderColor="mint.10">
							<Alert.Icon asChild={(iconProps) => <Microscope {...iconProps()} />} />
							<Alert.Content>
								<Alert.Title>Has primer</Alert.Title>
								<Alert.Description>
									This HVF has a primer signalling the possibility for a high risk
									reward
								</Alert.Description>
							</Alert.Content>
						</Alert.Root>
					</Show>

					<Show when={newSetup()?.scan_tool_valid}>
						<Alert.Root borderColor="mint.10">
							<Alert.Icon asChild={(iconProps) => <Ruler {...iconProps()} />} />
							<Alert.Content>
								<Alert.Title>Scan tool verified</Alert.Title>
								<Alert.Description>
									This HVF's data has been validated by the scan tool
								</Alert.Description>
							</Alert.Content>
						</Alert.Root>
					</Show>
				</Grid>

				<Button
					type="button"
					loading={createSetupResource.loading}
					children={isEditing() ? 'Update Setup' : 'Create Setup'}
					onClick={submitSetup}
				/>
			</Grid>
		</Show>
	)
}
